import React, { useState } from 'react'
import classNames from 'classnames'
import compact from 'lodash/compact'
import uniqBy from 'lodash/uniqBy'
import { Typography, Link, makeStyles } from 'transfix-ui/core'
import uuid from 'uuid'
import { brandColors, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { ActionsSelector, IActionsSelectorProps } from '../ActionsSelector'
import { FacilitySelector, IFacilitySelectorProps } from '../FacilitySelector'
import { OnHoldShipments } from '../OnHoldShipments'
import { useEdiMergeFacilityStepInfoQuery } from './graphql/EdiMergeFacilityStepInfo'

export interface IEdiMergeFacilityStepProps
  extends Pick<IActionsSelectorProps, 'defaultAction' | 'defaultAddVendor'>,
    Pick<IFacilitySelectorProps, 'defaultFacilityAction' | 'defaultFacility'> {
  shipmentId?: string
  shipperFacilityUuid: string
  shipperApplicationId?: string
  onFacilityChange: IFacilitySelectorProps['onChange']
  onActionChange: IActionsSelectorProps['onChange']
  disableMergeAction?: boolean
}

const useEdiMergeFacilityStepStyles = makeStyles(theme => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  codes: {
    display: 'inline-flex',
    gap: theme.spacing(2),
  },
  sectionWithBorder: {
    borderBottom: `1px solid ${brandColors.coolGray3}`,
  },
}))

export const EdiMergeFacilityStep = ({
  defaultAction,
  defaultAddVendor,
  defaultFacility,
  defaultFacilityAction,
  disableMergeAction,
  onActionChange,
  onFacilityChange,
  shipmentId,
  shipperApplicationId,
  shipperFacilityUuid,
}: IEdiMergeFacilityStepProps) => {
  const styles = useEdiMergeFacilityStepStyles()
  const hasShipmentId = Boolean(shipmentId)
  const { data } = useEdiMergeFacilityStepInfoQuery({
    variables: {
      shipmentId: shipmentId || '',
      shipperFacilityUuid,
      shipperApplicationId: shipperApplicationId || '',
      includeShipment: hasShipmentId,
      includeShipperFacilitiesIdentifiers: !hasShipmentId,
    },
  })

  const { facility, shipment, ediMatchingFacilities = [] } = data || {}
  const [selectedFacility, setSelectedFaciliy] = useState<
    IFacilitySelectorProps['defaultFacility'] | null
  >(defaultFacility || facility)
  const [currentAction, setCurrentAction] = useState<
    IFacilitySelectorProps['defaultFacilityAction']
  >(defaultFacilityAction)

  const { shipperFacilities, vendorFacilities = [] } = facility || {}
  const { vendorFacilities: selectedVendorFacilties = [] } = selectedFacility || {}
  const vendors = uniqBy(
    compact(
      [...(vendorFacilities || []), ...(selectedVendorFacilties || [])].map(({ vendor }) => vendor)
    ),
    'id'
  )
  const shipperFacilitiesIdentifiers = shipperFacilities?.flatMap(({ identifiers }) =>
    compact(identifiers).map(({ ediLocationCode, facilityCode, id }) => ({
      id,
      ediLocationCode,
      facilityCode,
    }))
  )

  const { stops = [] } = shipment || {}
  const { facilityIdentifier, stopType } =
    stops.find(({ shipperFacility }) => shipperFacility.uuid === shipperFacilityUuid) || {}

  const identifiers = hasShipmentId ? [facilityIdentifier] : shipperFacilitiesIdentifiers

  const ediWarningMessage =
    ediMatchingFacilities.length === 1
      ? 'Both facilities are using the following facility identifiers:'
      : 'The above facilities are using the following facility identifiers:'

  const handleFacilityChange: IFacilitySelectorProps['onChange'] = (newAction, newFacility) => {
    onFacilityChange(newAction, newFacility)
    setCurrentAction(newAction)
    setSelectedFaciliy(newFacility)
  }

  return (
    <div>
      <div className={classNames(styles.section, styles.sectionWithBorder)}>
        {hasShipmentId ? (
          <Typography variant='subtitle1'>
            Please select the correct address for the following {stopType} (
            <Link
              target='_blank'
              rel='noopener noreferrer'
              href={`${__ROOT__}/shipment-details/${shipmentId}`}>
              #{shipmentId}
            </Link>
            )
          </Typography>
        ) : (
          <Typography variant='subtitle1'>Please select the correct facility address</Typography>
        )}
        <FacilitySelector
          shipperFacilityUuid={shipperFacilityUuid}
          onChange={handleFacilityChange}
          hasShipmentId={hasShipmentId}
          defaultFacility={defaultFacility}
          defaultFacilityAction={defaultFacilityAction}
          shipperApplicationId={shipperApplicationId || ''}
        />
      </div>
      <div className={classNames(styles.section, styles.sectionWithBorder)}>
        <InlineMessage message={ediWarningMessage} type={INLINE_MESSAGE_VARIANTS.WARNING} />
        {identifiers?.map(identifier => (
          <div key={identifier?.id || uuid()} className={styles.codes}>
            <div>
              <Typography variant='caption' color='textSecondary'>
                EDI Location Code
              </Typography>
              <Typography variant='body2'>
                {identifier?.ediLocationCode || DISPLAY.empty}
              </Typography>
            </div>
            <div>
              <Typography variant='caption' color='textSecondary'>
                Shipper Facility Code
              </Typography>
              <Typography variant='body2'>{identifier?.facilityCode || DISPLAY.empty}</Typography>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.section}>
        <Typography variant='subtitle1'>How would you like to handle this address?</Typography>
        <ActionsSelector
          defaultAction={defaultAction}
          defaultAddVendor={defaultAddVendor}
          allowCreateVendor={hasShipmentId}
          vendors={vendors}
          onChange={onActionChange}
          disableMergeAction={disableMergeAction}
          facilityAction={currentAction}
        />
      </div>
      <OnHoldShipments shipperFacilityUuid={shipperFacilityUuid} shipmentId={shipmentId || ''} />
    </div>
  )
}
